<template>
  <div>
    <!-- Edit Hike List Modal -->
    <div class="modal fade" id="editHikeListModal" tabindex="-1" aria-labelledby="editHikeListModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content p-3">
          <div class="modal-header">
            <h5 class="modal-title" id="editHikeListModalLabel"><strong>Edit Hike List</strong></h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label for="listName" class="form-label">List Name</label>
              <input type="text" v-model="editForm.name" class="form-control" id="listName" required>
            </div>
            <div class="mb-3">
              <label for="listDescription" class="form-label">Description (optional)</label>
              <textarea v-model="editForm.description" class="form-control" id="listDescription" rows="3"></textarea>
            </div>
            <div class="mb-3">
              <label for="listImages" class="form-label">Images</label>

              <!-- Existing Images -->
              <div v-if="existingImages.length > 0" class="mb-3">
                <small class="text-muted d-block mb-2">Current images (drag to reorder):</small>
                <div class="d-flex flex-wrap gap-2">
                  <div
                    v-for="(image, index) in existingImages"
                    :key="image.id"
                    class="position-relative rounded overflow-hidden image-draggable"
                    :class="{ 'dragging': draggingIndex === index, 'drag-over': dragOverIndex === index }"
                    style="width: 90px; height: 90px; border: 1px solid #dee2e6; cursor: grab;"
                    draggable="true"
                    @dragstart="handleDragStart($event, index)"
                    @dragend="handleDragEnd"
                    @dragover.prevent="handleDragOver($event, index)"
                    @drop.prevent="handleDrop(index)"
                  >
                    <img
                      :src="image.file.smUrl || image.file.lgUrl"
                      alt="List image"
                      class="w-100 h-100"
                      style="object-fit: cover; pointer-events: none;"
                    >

                    <!-- Cover Photo Badge — full-width strip pinned to the bottom edge,
                         matching PlanTrailSaveModal.vue's .cover-badge styling. -->
                    <div
                      v-if="index === 0"
                      class="hike-list-cover-badge"
                    >Cover photo</div>

                    <button
                      type="button"
                      class="btn-close position-absolute top-0 end-0 m-1"
                      @click="deleteExistingImage(image.id)"
                      :disabled="deletingImageIds.includes(image.id)"
                      aria-label="Delete image"
                      style="background-color: rgba(255, 255, 255, 0.8);"
                    ></button>
                    <div
                      v-if="deletingImageIds.includes(image.id)"
                      class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                      style="background-color: rgba(0, 0, 0, 0.5);"
                    >
                      <div class="spinner-border spinner-border-sm text-light" role="status">
                        <span class="visually-hidden">Deleting...</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- New Images Upload -->
              <input
                type="file"
                class="form-control"
                id="listImages"
                accept="image/*"
                multiple
                @change="handleImageSelection"
                ref="fileInput"
              >
              <div class="form-text">Add new photos or remove existing ones above.</div>
              <div v-if="selectedImages.length > 0" class="d-flex flex-wrap gap-2 mt-2">
                <div
                  v-for="(image, index) in selectedImages"
                  :key="index"
                  class="position-relative rounded overflow-hidden"
                  style="width: 90px; height: 90px; border: 1px solid #dee2e6;"
                >
                  <img
                    v-if="image.previewUrl"
                    :src="image.previewUrl"
                    alt="Selected image"
                    class="w-100 h-100"
                    style="object-fit: cover;"
                  >
                  <div v-else class="w-100 h-100 d-flex align-items-center justify-content-center bg-light text-muted">
                    ...
                  </div>

                  <!-- Cover Photo Badge — only when there are no existing images;
                       otherwise the cover sits on the first existing image above.
                       Full-width strip pinned to the bottom edge. -->
                  <div
                    v-if="index === 0 && existingImages.length === 0"
                    class="hike-list-cover-badge"
                  >Cover photo</div>

                  <button
                    type="button"
                    class="btn-close position-absolute top-0 end-0 m-1"
                    @click="removeImage(index)"
                    aria-label="Remove image"
                  ></button>
                  <div
                    v-if="image.status === 'uploading'"
                    class="position-absolute top-50 start-50 translate-middle"
                  >
                    <div class="spinner-border spinner-border-sm text-light" role="status">
                      <span class="visually-hidden">Uploading...</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="form-check mb-3">
              <input type="checkbox" v-model="editForm.publiclyVisible" class="form-check-input" id="publiclyVisible">
              <label class="form-check-label" for="publiclyVisible">Make this list public</label>
            </div>
            <div class="d-flex justify-content-end">
              <button type="button" class="btn btn-secondary me-2" data-bs-dismiss="modal">Cancel</button>
              <button type="button" class="btn btn-primary" @click="updateList" :disabled="updating">
                {{ updating ? 'Saving...' : 'Save Changes' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Hike Modal -->
    <div class="modal fade" id="addHikeModal" tabindex="-1" aria-labelledby="addHikeModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content p-3">
          <div class="modal-header">
            <h5 class="modal-title" id="addHikeModalLabel"><strong>Add Hike to List</strong></h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="row mb-3">
              <div class="col-12">
                <div class="input-group hero-search mt-2 py-2">
                  <input
                    type="text"
                    v-model="searchQuery"
                    class="form-control search-bar__input pl-3"
                    placeholder="Search for hikes"
                    id="hike-search-input"
                  >
                  <span class="input-group-append d-flex align-items-center">
                    <span class="input-group-text">
                      <span class="material-icons">search</span>
                    </span>
                  </span>
                </div>
              </div>
            </div>

            <!-- Loading state -->
            <div v-if="searching" class="text-center py-4">
              <div class="spinner-border text-light" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>

            <!-- Results tabs -->
            <div v-if="!searching && searchQuery" class="mb-3">
              <ul class="nav nav-tabs" role="tablist">
                <li class="nav-item m-0" role="presentation">
                  <button
                    class="nav-link active"
                    id="official-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#official"
                    type="button"
                  >
                    HiiKER Hikes ({{ officialHikes.length }})
                  </button>
                </li>
                <li class="nav-item m-0" role="presentation">
                  <button
                    class="nav-link"
                    id="user-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#user"
                    type="button"
                  >
                    My Hikes ({{ userHikes.length }})
                  </button>
                </li>
              </ul>

              <div class="tab-content mt-3 big-list">
                <!-- Official Hikes Tab -->
                <div class="tab-pane fade show active overflow-auto" id="official" style="max-height: 400px;">
                  <div v-if="officialHikes.length === 0" class="text-center text-muted py-4">
                    No official hikes found
                  </div>
                  <div
                    v-for="(hit, index) in officialHikes"
                    :key="hit.document.id"
                    class="result-item cursor-pointer"
                    @click="addHike(hit.document.id)"
                  >
                    <div class="compact-list d-flex gap-4 align-items-center justify-content-between px-3 py-2">
                      <div class="d-flex gap-3">
                        <img
                          :src="hit.document.square_static_map_image_url || require('../../images/placeholder_thumbnailable.jpg')"
                          class="img-fluid rounded w-100 square"
                          alt=""
                          loading="lazy"
                        >

                        <div class="d-flex flex-column">
                          <p class="mb-0"><strong>{{ hit.document.name }}</strong></p>
                          <p class="mb-1">
                            <small class="text-muted">
                              {{ hit.document.region_name }}, {{ hit.document.country_name }}
                            </small>
                          </p>
                          <p class="m-0 text-yellow">
                            <small>
                              <span v-if="hit.document.distance_meters">
                                {{ (hit.document.distance_meters / 1000).toFixed(1) }} km
                              </span>
                              <span v-if="hit.document.distance_meters && hit.document.elevation_gain_meters" class="mx-1">&#183;</span>
                              <span v-if="hit.document.elevation_gain_meters">
                                {{ hit.document.elevation_gain_meters }}m elevation
                              </span>
                            </small>
                          </p>
                        </div>
                      </div>
                      <span class="material-icons add-hover">add_circle</span>
                    </div>
                    <hr v-if="index < officialHikes.length - 1" class="my-2">
                  </div>
                </div>

                <!-- User Hikes Tab -->
                <div class="tab-pane fade overflow-auto" id="user" style="max-height: 400px;">
                  <div v-if="userHikes.length === 0" class="text-center text-muted py-4">
                    No personal hikes found
                  </div>
                  <div
                    v-for="(hike, index) in userHikes"
                    :key="hike.id"
                    class="result-item cursor-pointer"
                    @click="addHike(hike.id)"
                  >
                    <div class="compact-list d-flex gap-4 align-items-center">
                      <img
                        :src="hike.staticMapImageUrl || require('../../images/placeholder_thumbnailable.jpg')"
                        class="img-fluid rounded w-100 square"
                        alt=""
                        loading="lazy"
                      >
                      <div class="d-flex flex-column">
                        <p class="mb-0"><strong>{{ hike.name }}</strong></p>
                        <p class="mb-1">
                          <small class="text-muted">
                            {{ hike.mainRegion?.name }}, {{ hike.mainCountry?.name }}
                          </small>
                        </p>
                        <p class="m-0 text-yellow">
                          <small>
                            <span v-if="hike.distanceMeters">
                              {{ (hike.distanceMeters / 1000).toFixed(1) }} km
                            </span>
                            <span v-if="hike.distanceMeters && hike.elevationGainMeters" class="mx-1">&#183;</span>
                            <span v-if="hike.elevationGainMeters">
                              {{ hike.elevationGainMeters }}m elevation
                            </span>
                          </small>
                        </p>
                      </div>
                    </div>
                    <hr v-if="index < userHikes.length - 1" class="my-2">
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty state -->
            <div v-if="!searching && !searchQuery" class="text-center text-muted py-4">
              <p>Start typing to search for hikes</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, onMounted } from "vue";
import { TypesenseResponse } from "../../utils/models/TypesenseResponse";
import graph_client from "../../utils/graphql/graph_client";
import { SearchUserHikesSimple } from "../../utils/graphql/hikes-graph";
import { GetHikeList, UpdateHikeList, AddHikeToList } from "../../utils/graphql/hike-list-graph";
import { GenerateDirectUpload } from "../../utils/graphql/comments-graph";
import { DestroyImage } from "../../utils/graphql/image-graph";
import { Modal } from "bootstrap";
import SparkMD5 from "spark-md5";
import { buildImageAttributesWithCover, KeptImage } from "../../utils/buildImageAttributesWithCover";

export default defineComponent({
  props: {
    hikeListSlug: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const hikeList = ref<any>(null);
    const searchQuery = ref("");
    const searching = ref(false);
    const updating = ref(false);
    const adding = ref(false);
    const officialHikes = ref<any[]>([]);
    const userHikes = ref<any[]>([]);
    const fileInput = ref<HTMLInputElement | null>(null);
    const selectedImages = ref<any[]>([]);
    const existingImages = ref<any[]>([]);
    const deletingImageIds = ref<string[]>([]);
    const draggingIndex = ref<number | null>(null);
    const dragOverIndex = ref<number | null>(null);

    const editForm = ref({
      name: "",
      description: "",
      publiclyVisible: false
    });

    const loadHikeList = async () => {
      try {
        const response = await graph_client.request(GetHikeList, {
          obfuscatedSlug: props.hikeListSlug
        });
        hikeList.value = response.hikeList;

        // Populate edit form
        if (hikeList.value) {
          editForm.value = {
            name: hikeList.value.name,
            description: hikeList.value.description || "",
            publiclyVisible: hikeList.value.publiclyVisible
          };
          existingImages.value = hikeList.value.images || [];
        }
      } catch (error) {
        console.error("Error loading hike list:", error);
      }
    };

    const performSearch = async (query: string) => {
      if (!query || query.trim().length === 0) {
        officialHikes.value = [];
        userHikes.value = [];
        return;
      }

      searching.value = true;

      try {
        // Search official hikes via Typesense
        const filterString = "made_official_at:>0";
        const typesenseResponse = await TypesenseResponse.get(query, filterString, null, 1, 20);
        if (typesenseResponse) {
          officialHikes.value = typesenseResponse.hits;
        } else {
          officialHikes.value = [];
        }

        // Search user's hikes via GraphQL
        if (window.user) {
          const hikesResponse = await graph_client.request(SearchUserHikesSimple, {
            query: query,
            page: 1,
            perPage: 20
          });
          userHikes.value = hikesResponse.searchUserHikes || [];
        } else {
          userHikes.value = [];
        }
      } catch (error) {
        console.error("Error searching hikes:", error);
        officialHikes.value = [];
        userHikes.value = [];
      } finally {
        searching.value = false;
      }
    };

    watch(searchQuery, (newVal) => {
      performSearch(newVal);
    });

    const generateClientId = () => {
      if (window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
      }
      return `img_${Math.random().toString(36).slice(2, 10)}`;
    };

    const calculateMD5Base64 = async (file: File): Promise<string> => {
      const buffer = await file.arrayBuffer();
      const spark = new SparkMD5.ArrayBuffer();
      spark.append(buffer);
      const hashHex = spark.end();
      const bytePairs = hashHex.match(/.{2}/g) || [];
      const bytes = new Uint8Array(bytePairs.map((pair) => parseInt(pair, 16)));
      let binary = '';
      bytes.forEach((byte) => {
        binary += String.fromCharCode(byte);
      });
      return window.btoa(binary);
    };

    const uploadImageAndGetSignedId = async (imageEntry: any): Promise<string> => {
      imageEntry.status = 'uploading';

      const checksumBase64 = await calculateMD5Base64(imageEntry.file);

      const uploadResponse = await graph_client.request(GenerateDirectUpload, {
        input: {
          filename: imageEntry.file.name,
          byteSize: imageEntry.file.size,
          contentType: imageEntry.file.type || 'image/jpeg',
          checksumBase64,
          serviceName: window.publicBucketService
        }
      });

      const uploadData = uploadResponse.generateDirectUpload;
      if (!uploadData) {
        throw new Error('Missing upload credentials.');
      }

      const directUploadHeaders = uploadData.headers ? JSON.parse(uploadData.headers) : {};
      const directUploadResult = await fetch(uploadData.uploadUrl, {
        method: 'PUT',
        headers: {
          ...directUploadHeaders,
          'Content-Type': imageEntry.file.type || 'image/jpeg'
        },
        body: imageEntry.file
      });

      if (!directUploadResult.ok) {
        throw new Error(`Upload failed with status ${directUploadResult.status}.`);
      }

      imageEntry.status = 'uploaded';
      return uploadData.signedId;
    };

    const handleImageSelection = (event: Event) => {
      const input = event.target as HTMLInputElement;
      const files = Array.from(input.files || []);

      files.forEach((file) => {
        const imageEntry = {
          file,
          clientId: generateClientId(),
          signedId: null,
          status: 'pending',
          previewUrl: ''
        };

        const reader = new FileReader();
        reader.onload = (loadEvent) => {
          imageEntry.previewUrl = (loadEvent.target?.result as string) || '';
        };
        reader.readAsDataURL(file);

        selectedImages.value.push(imageEntry);
      });

      // Clear input so the same file can be selected again
      if (input) {
        input.value = '';
      }
    };

    const removeImage = (index: number) => {
      selectedImages.value.splice(index, 1);
    };

    const deleteExistingImage = async (imageId: string) => {
      if (!confirm('Are you sure you want to delete this image?')) {
        return;
      }

      deletingImageIds.value.push(imageId);

      try {
        await graph_client.request(DestroyImage, {
          image: { id: imageId }
        });

        // Remove from existing images array
        existingImages.value = existingImages.value.filter(img => img.id !== imageId);
      } catch (error) {
        console.error("Error deleting image:", error);
        alert("Failed to delete image");
      } finally {
        deletingImageIds.value = deletingImageIds.value.filter(id => id !== imageId);
      }
    };

    const handleDragStart = (event: DragEvent, index: number) => {
      draggingIndex.value = index;
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', index.toString());
      }
    };

    const handleDragEnd = () => {
      draggingIndex.value = null;
      dragOverIndex.value = null;
    };

    const handleDragOver = (event: DragEvent, index: number) => {
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'move';
      }
      dragOverIndex.value = index;
    };

    const handleDrop = (dropIndex: number) => {
      if (draggingIndex.value === null || draggingIndex.value === dropIndex) {
        dragOverIndex.value = null;
        return;
      }

      // Reorder the images array
      const draggedImage = existingImages.value[draggingIndex.value];
      const newImages = [...existingImages.value];

      // Remove dragged item
      newImages.splice(draggingIndex.value, 1);

      // Insert at new position
      newImages.splice(dropIndex, 0, draggedImage);

      existingImages.value = newImages;

      draggingIndex.value = null;
      dragOverIndex.value = null;
    };

    const ensureSelectedImagesUploaded = async () => {
      for (const imageEntry of selectedImages.value) {
        if (imageEntry.signedId) continue;
        try {
          imageEntry.signedId = await uploadImageAndGetSignedId(imageEntry);
        } catch (error) {
          imageEntry.status = 'pending';
          throw error;
        }
      }
    };

    const updateList = async () => {
      if (updating.value || !hikeList.value) return;

      updating.value = true;

      try {
        await ensureSelectedImagesUploaded();

        const variables: any = {
          id: hikeList.value.id,
          name: editForm.value.name,
          description: editForm.value.description,
          publiclyVisible: editForm.value.publiclyVisible
        };

        // Order matters: the first kept image becomes the cover photo. Existing
        // images come first in their drag-reordered order, then newly uploaded
        // ones append at the end. The helper marks index 0 as isCover.
        const kept: KeptImage[] = [
          ...existingImages.value.map((image: any) => ({
            kind: 'existing' as const,
            id: image.id,
            clientId: `existing_${image.id}`
          })),
          ...selectedImages.value.map((imageEntry: any) => ({
            kind: 'new-signed' as const,
            signedId: imageEntry.signedId,
            clientId: imageEntry.clientId
          }))
        ];

        const allImagesAttributes = buildImageAttributesWithCover(kept, [], {
          generateClientId
        });

        if (allImagesAttributes.length > 0) {
          variables.imagesAttributes = allImagesAttributes;
        }

        const response = await graph_client.request(UpdateHikeList, variables);

        if (response.updateHikeList.errors.length === 0) {
          // Close modal
          const modalElement = document.getElementById('editHikeListModal');
          if (modalElement) {
            const modal = Modal.getInstance(modalElement);
            modal?.hide();
          }

          // Reload page
          window.location.reload();
        } else {
          alert(response.updateHikeList.errors.map((e: any) => e.errors.join(', ')).join('\n'));
        }
      } catch (error) {
        console.error("Error updating hike list:", error);
        alert("Failed to update hike list");
      } finally {
        updating.value = false;
      }
    };

    const addHike = async (hikeId: string) => {
      if (adding.value || !hikeList.value) return;

      adding.value = true;

      try {
        const response = await graph_client.request(AddHikeToList, {
          hikeListId: hikeList.value.id,
          hikeId: hikeId,
          notes: ""
        });

        if (response.addHikeToList.errors.length === 0) {
          // Close modal
          const modalElement = document.getElementById('addHikeModal');
          if (modalElement) {
            const modal = Modal.getInstance(modalElement);
            modal?.hide();
          }

          // Reload page
          window.location.reload();
        } else {
          alert(response.addHikeToList.errors.map((e: any) => e.errors.join(', ')).join('\n'));
        }
      } catch (error) {
        console.error("Error adding hike:", error);
        alert("Failed to add hike to list");
      } finally {
        adding.value = false;
      }
    };

    onMounted(() => {
      loadHikeList();
    });

    return {
      hikeList,
      editForm,
      searchQuery,
      searching,
      updating,
      adding,
      officialHikes,
      userHikes,
      fileInput,
      selectedImages,
      existingImages,
      deletingImageIds,
      draggingIndex,
      dragOverIndex,
      handleImageSelection,
      removeImage,
      deleteExistingImage,
      handleDragStart,
      handleDragEnd,
      handleDragOver,
      handleDrop,
      updateList,
      addHike
    };
  }
});
</script>

<style scoped>
  .modal-content{
    height: 84vh;
  }

  .result-item{
    cursor: pointer;
  }

  .add-hover:hover{
    color: var(--yellow);
  }

  /* Drag and drop styles */
  .image-draggable {
    transition: all 0.2s ease;
  }

  .image-draggable:active {
    cursor: grabbing !important;
  }

  .image-draggable.dragging {
    opacity: 0.4;
    transform: scale(0.95);
  }

  .image-draggable.drag-over {
    border: 2px solid #FBCD01 !important;
    box-shadow: 0 0 10px rgba(251, 205, 1, 0.5);
  }

  .image-draggable:hover:not(.dragging) {
    border-color: #FBCD01 !important;
  }

  /* Mirrors .cover-badge in PlanTrailSaveModal.vue so the "Cover photo"
     indicator looks identical between creating a hike and creating/editing
     a hike list. */
  .hike-list-cover-badge {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(245, 221, 66, 0.92);
    color: #17181d;
    font-size: 0.6rem;
    font-weight: 700;
    padding: 3px 0;
    text-align: center;
    pointer-events: none;
  }
</style>
